import React from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import styles from './BlogTitlesModalForm.module.css';
import Modal from '../Modal/Modal';
import Button from '../Button/Button';

const BADGE_COLORS = {
  'Most Popular': 'badge-popular',
  "Wizard's Pick": 'badge-wizard',
  'SEO Booster': 'badge-seo',
  'Traffic Builder': 'badge-traffic',
  'Expert Pick': 'badge-expert',
  'Trending': 'badge-trending',
  'Best of the Best': 'badge-best',
  'Top Picks': 'badge-toppicks',
  'Must-Read': 'badge-mustread',
  'Must-See': 'badge-mustsee',
  'Must-Try': 'badge-musttry',
  'Must-Know': 'badge-mustknow',
  'Must-Do': 'badge-mustdo',
  'Must-Have': 'badge-musthave',
};

const CloseIcon = props => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" {...props}>
    <circle cx="12" cy="12" r="12" fill="#F2F4F7" />
    <path d="M15.3 8.7a1 1 0 0 0-1.4-1.4L12 9.59 10.1 7.7A1 1 0 0 0 8.7 9.1l1.89 1.89-1.89 1.89a1 1 0 1 0 1.41 1.41l1.89-1.89 1.89 1.89a1 1 0 0 0 1.41-1.41l-1.89-1.89 1.89-1.89z" fill="#667085" />
  </svg>
);

const BlogTitlesModalForm = ({
  generateBlogsInProgress,
  generateBlogsError,
  onManageDisableScrolling,
  isOpen,
  onClose,
  options,
  maxSelectable,
  onSubmit,
  initialSelected = [],
  title = 'Boost Your Listing with Blogs',
  subtitle = 'Select the blog topics you’d like us to write. These are Wizard-picked to help boost your SEO and bookings.',
}) => {
  const validate = values => {
    const selected = values.selectedTitles || [];
    if (selected.length > maxSelectable) {
      return { selectedTitles: `You can select up to ${maxSelectable} blog posts.` };
    }
    return {};
  };

  return (
    <Modal id="blogTitlesModal" isOpen={isOpen} onClose={onClose} onManageDisableScrolling={onManageDisableScrolling}>
      {generateBlogsError && (
        <div>
          <p className={styles.errorText}>Something went wrong while generating blogs</p>
        </div>
      )}
      <div className={styles.modalHeader}>
        <div className={styles.titleRow}>
          <span className={styles.titleIcon}><i className="ri-sparkling-line"></i></span>
          <div>
            <span className={styles.title}>{title}</span>
            <div className={styles.subtitle}>{subtitle}</div>
          </div>
        </div>
      </div>
      {generateBlogsInProgress ?<div className={styles.loadingContainer}>
        <p className={styles.loadingTitle}>Hang tight—we’re generating your blogs..</p>
        <p className={styles.loadingDesc}>This may take a few minutes. Please keep the page open.</p>
        <div className={styles.cardContainer}>
          <div className={styles.card}>
            <div className={styles.cardImage}>

              <div className={styles.loadingIcon}>
                <span className={styles.spinnerWrapper}>
                  <svg className={styles.spinnerTrack} width="48" height="48" viewBox="0 0 48 48">
                    <circle
                      className={styles.spinnerTrackCircle}
                      cx="24"
                      cy="24"
                      r="20"
                      fill="none"
                      stroke="#ECECEC"
                      strokeWidth="2"
                    />
                    <circle
                      className={styles.spinnerArc}
                      cx="24"
                      cy="24"
                      r="20"
                      fill="none"
                      stroke="#F2704E"
                      strokeWidth="2"
                      strokeDasharray="100"
                      strokeDashoffset="25"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span className={styles.innerIcon}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M6.41421 15.89L16.5563 5.74786L15.1421 4.33364L5 14.4758V15.89H6.41421ZM7.24264 17.89H3V13.6473L14.435 2.21232C14.8256 1.8218 15.4587 1.8218 15.8492 2.21232L18.6777 5.04075C19.0682 5.43127 19.0682 6.06444 18.6777 6.45496L7.24264 17.89ZM3 19.89H21V21.89H3V19.89Z" fill="#D5D7DA" />
                    </svg>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div> :
      <Form
        onSubmit={onSubmit}
        initialValues={{ selectedTitles: initialSelected }}
        validate={validate}
        render={({ handleSubmit, values, errors, submitting }) => {
          const selected = values.selectedTitles || [];
          return (
            <form onSubmit={handleSubmit}>
              <div className={styles.formFieldsContainer}>
                <div className={styles.limitRow}>
                  <span>
                    You can create up to <b>{maxSelectable} blog posts</b> based on your item’s value.
                  </span>
                  <span className={styles.selectedCount}>{selected.length}/{maxSelectable} Selected</span>
                </div>
                <div className={styles.options}>
                  {options.map((opt, idx) => (
                    <label className={[
                      styles.option,
                      selected.includes(opt.value) ? styles.selected : '',
                    ].join(' ')} key={opt.value}>
                      <Field
                        name="selectedTitles"
                        component="input"
                        type="checkbox"
                        value={opt.value}
                        disabled={
                          !selected.includes(opt.value) && selected.length >= maxSelectable
                        }
                      />
                      {selected.includes(opt.value) ? <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M0 4C0 1.79086 1.79086 0 4 0H12C14.2091 0 16 1.79086 16 4V12C16 14.2091 14.2091 16 12 16H4C1.79086 16 0 14.2091 0 12V4Z" fill="#F2704E" />
                        <path d="M6.99985 9.58545L11.5961 4.98926L12.3032 5.69636L6.99985 10.9996L3.81787 7.8177L4.52498 7.1106L6.99985 9.58545Z" fill="white" />
                      </svg>
                        : <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 0.5H14C17.0376 0.5 19.5 2.96243 19.5 6V14C19.5 17.0376 17.0376 19.5 14 19.5H6C2.96243 19.5 0.5 17.0376 0.5 14V6C0.5 2.96243 2.96243 0.5 6 0.5Z" fill="#FAFAFA" />
                          <path d="M6 0.5H14C17.0376 0.5 19.5 2.96243 19.5 6V14C19.5 17.0376 17.0376 19.5 14 19.5H6C2.96243 19.5 0.5 17.0376 0.5 14V6C0.5 2.96243 2.96243 0.5 6 0.5Z" stroke="#D5D7DA" />
                        </svg>
                      }
                      <div className={styles.optionContent}>
                        <div className={styles.optionTitle}>
                          <span >{opt.label}</span>

                        </div>
                        <div className={styles.optionTitleRow}>
                          <div className={styles.optionMeta}>
                            <i className="ri-file-text-line"></i> ~{opt.words || 1000} words
                          </div>
                          {opt.badge && (
                            <span className={`${styles.badge} ${styles[BADGE_COLORS[opt.badge]] || ''}`}>{opt.badge}</span>
                          )}

                        </div>
                      </div>
                    </label>
                  ))}
                </div>
                {errors.selectedTitles && (
                  <div className={styles.error}>{errors.selectedTitles}</div>
                )}
              </div>
              <div className={styles.modalButtonsContainer}>
                <Button variant="info" onClick={onClose} disabled={submitting || generateBlogsInProgress} style={{paddingLeft: 0}}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className={styles.submit}
                  disabled={submitting || selected.length === 0 || selected.length > maxSelectable || generateBlogsInProgress}
                >
                  {generateBlogsInProgress ? 'Generating Blogs...' : 'Generate Blogs'}
                </Button>
              </div>
            </form>
          );
        }}
      />}
    </Modal>
  );
};

BlogTitlesModalForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      badge: PropTypes.string,
      words: PropTypes.number,
    })
  ).isRequired,
  maxSelectable: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialSelected: PropTypes.array,
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

export default BlogTitlesModalForm;
